<template>
  <div class="evaluation-deck" :class="{ 'evaluation-deck--loading': isOperationInProgress }">
    <div v-if="isOperationInProgress" class="evaluation-deck__progress">
      <v-progress-circular class="theme-loader" indeterminate></v-progress-circular>
    </div>
    <div v-if="!showFinish && !showValidation && !showExamValidation">
      <div
        :is="currentEvaluation.componentName"
        :key="currentEvaluation.id"
        :evaluation="currentEvaluation"
        :is-submitted="isCurrentEvaluationSubmitted"
        @evaluateEvent="evaluateEventHandler"
      ></div>
      <nav
        v-if="hasMultipleEvaluations"
        class="evaluation-deck__navigation"
        aria-label="Навігація між питаннями"
      >
      <button
        class="evaluation-deck__navigation-button"
        type="button"
        :disabled="disablePrev"
        @click="prevQuestion"
      >
        <v-icon size="19">mdi-arrow-left</v-icon>
        Попереднє питання
      </button>
      <button
        class="evaluation-deck__navigation-button evaluation-deck__navigation-button--next"
        type="button"
        :disabled="disableNext"
        @click="nextQuestion"
      >
        Наступне питання
        <v-icon size="19">mdi-arrow-right</v-icon>
      </button>
      </nav>
    </div>
    <div class="evaluation-deck__result" v-else-if="showFinish">
      <span><v-icon color="#23936f" size="28">mdi-check</v-icon></span>
      <div><strong>Завдання виконано</strong><p>Відповідь успішно збережено.</p></div>
    </div>
    <div class="evaluation-deck__result" v-else-if="showValidation">
      <span><v-icon color="#b56a00" size="27">mdi-clock-outline</v-icon></span>
      <div><strong>Відповідь надіслано</strong><p>Очікуємо оцінку вчителя.</p></div>
    </div>
    <div class="evaluation-deck__result" v-else-if="showExamValidation">
      <span><v-icon color="#b56a00" size="27">mdi-clock-outline</v-icon></span>
      <div><strong>Роботу надіслано</strong><p>Очікуємо оцінку вчителя.</p></div>
    </div>
    <button
      class="evaluation-deck__submit"
      v-if="isExam && !showExamValidation"
      type="button"
      :disabled="disableSend"
      @click="sendEvaluations"
    >
      Надіслати відповіді
      <v-icon size="19">mdi-send</v-icon>
    </button>
    <v-snackbar v-model="snackbar" class="evaluation-deck__toast" bottom :timeout="-1">
      <div class="evaluation-deck__toast-content">
        <span><v-icon size="21">mdi-alert-circle-outline</v-icon></span>
        <div>
          <strong>Перевірте відповідь</strong>
          <p>{{ toasterText }}</p>
        </div>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn icon v-bind="attrs" aria-label="Закрити повідомлення" @click="closeToaster">
          <v-icon size="20">mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import evaluationEventEnum from '@/enums/evaluationEventEnum';

import QuizEvaluation from '@/components/Base/QuizEvaluation/QuizEvaluation';
import TaskEvaluation from '@/components/Base/TaskEvaluation/TaskEvaluation';
import SelectAnswerEvaluation from '@/components/Base/SelectAnswerEvaluation/SelectAnswerEvaluation';
import AddFileEvaluation from '@/components/Base/AddFileEvaluation/AddFileEvaluation';
export default {
  name: 'EvaluationDeck',
  components: {
    QuizEvaluation,
    TaskEvaluation,
    SelectAnswerEvaluation,
    AddFileEvaluation,
  },
  props: {
    isExam: {
      type: Boolean,
      required: false,
      default: false,
    },
    evaluationDeck: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      disableNext: false,
      disablePrev: false,
      disableSend: false,
      currentEvaluation: {},
      showFinish: false,
      showValidation: false,
      showExamValidation: false,
      examSent: false,
      submittedEvaluationIds: [],
      snackbar: false,
      toasterTimeout: null,
      toasterText: '',
    };
  },

  created() {
    this.resetEvaluationDeck();
  },

  watch: {
    evaluationDeck() {
      this.resetEvaluationDeck();
    },
  },

  computed: {
    hasMultipleEvaluations() {
      return this.evaluationDeck.getEvaluations().length > 1;
    },
    isOperationInProgress() {
      return this.$store.getters['CurrentLessonStore/isOperationInProgress'];
    },
    isCurrentEvaluationSubmitted() {
      return Boolean(
        this.currentEvaluation &&
        this.submittedEvaluationIds.includes(this.currentEvaluation.id)
      );
    },
  },

  methods: {
    resetEvaluationDeck() {
      this.disableNext = false;
      this.disablePrev = false;
      this.disableSend = false;
      this.showFinish = false;
      this.showValidation = false;
      this.showExamValidation = false;
      this.examSent = false;
      this.submittedEvaluationIds = [];
      this.currentEvaluation = this.evaluationDeck.getCurrentEvaluation();
      if (!this.currentEvaluation) {
        return;
      }
      this.updaButtons();
    },
    async sendEvaluations() {
      try {
        await this.$store.dispatch('CurrentLessonStore/submitTest', {
          evaluations: this.evaluationDeck.getEvaluations(),
        });
        this.examSent = true;
        this.updaButtons();
      } catch (error) {
        console.error(`Get error on send evaluations error^${error}`);
        this.showTaster(`помилка ${error}`);
      }
    },
    showTaster(message) {
      if (this.toasterTimeout) {
        clearTimeout(this.toasterTimeout);
        this.toasterTimeout = null;
      }
      this.snackbar = true;
      this.toasterText = message;
      this.toasterTimeout = setTimeout(() => {
        this.snackbar = false;
      }, 5000);
    },
    closeToaster() {
      if (this.toasterTimeout) {
        clearTimeout(this.toasterTimeout);
        this.toasterTimeout = null;
      }
      this.snackbar = false;
    },
    async sendEvaluation(evaluation) {
      try {
        const isValidAnswer = await this.$store.dispatch(
          'CurrentLessonStore/submitOneTest',
          {
            evaluation,
          }
        );
        return isValidAnswer;
      } catch (error) {
        console.error(`помилка ${error}`);
        this.showTaster('Помилка при загрузці файлів');
        return null;
      }
    },
    checkFinish() {
      if (!this.currentEvaluation.hasAnswer()) {
        return;
      }
      this.showFinish =
        !this.isExam &&
        !this.$store.getters['CurrentLessonStore/hasValidationQuestions'] &&
        this.evaluationDeck.isLast();
      this.showValidation =
        !this.isExam &&
        this.$store.getters['CurrentLessonStore/hasValidationQuestions'] &&
        this.evaluationDeck.isLast();
      this.showExamValidation =
        this.examSent && this.isExam && this.evaluationDeck.isLast();
    },
    updaButtons(isValidAnswer) {
      this.disableNext =
        !this.currentEvaluation.hasAnswer() ||
        this.evaluationDeck.isLast() ||
        (!isValidAnswer && !this.isExam);

      this.disablePrev = this.evaluationDeck.isFirst();
      this.disableSend =
        !this.evaluationDeck.isLast() || !this.currentEvaluation.hasAnswer();
      this.checkFinish();
    },
    async evaluateEventHandler(payload) {
      const { type, data } = payload;
      this.evaluationDeck.setUserAnswer(data.answer);
      switch (type) {
        case evaluationEventEnum.SEND_ANSWER:
          if (this.isExam) {
            this.updaButtons();
            return;
          }
          const isValidAnswer = await this.sendEvaluation(
            this.currentEvaluation
          );
          if (!isValidAnswer && isValidAnswer !== null) {
            this.showTaster('Неправильна відповідь на питання');
            return;
          } else if (isValidAnswer === null) {
            return;
          }
          if (!this.submittedEvaluationIds.includes(this.currentEvaluation.id)) {
            this.submittedEvaluationIds.push(this.currentEvaluation.id);
          }
          this.updaButtons(isValidAnswer);
          return;
        case evaluationEventEnum.SHOW_TOASTER:
          this.showTaster(data.text);
        default:
          break;
      }
    },
    nextQuestion() {
      this.currentEvaluation = this.evaluationDeck.moveNextEvaluation();
      this.updaButtons(true);
    },
    prevQuestion() {
      this.currentEvaluation = this.evaluationDeck.movePrevEvaluation();
      this.updaButtons(true);
    },
  },
};
</script>

<style lang="less" scoped>
.evaluation-deck {
  min-width: 0;
  position: relative;
}
.evaluation-deck--loading {
  opacity: 0.5;
  pointer-events: none;
}
.evaluation-deck__progress {
  align-items: center;
  display: flex;
  inset: 0;
  justify-content: center;
  position: absolute;
  z-index: 10;
}
.evaluation-deck__navigation {
  display: flex;
  gap: 10px;
  margin-top: 20px;
}
.evaluation-deck__navigation-button {
  align-items: center;
  background: var(--surface-primary);
  border: 1px solid var(--theme-border-decorative);
  border-radius: 10px;
  color: var(--theme-banner-text);
  cursor: pointer;
  display: inline-flex;
  font-size: 13px;
  font-weight: 750;
  gap: 6px;
  min-height: 42px;
  padding: 9px 14px;
}
.evaluation-deck__navigation-button:hover:not(:disabled) {
  background: var(--theme-banner-background);
  border-color: var(--theme-header-background);
  color: var(--theme-banner-text);
}
.evaluation-deck__navigation-button--next:not(:disabled) {
  background: var(--theme-header-background);
  border-color: var(--theme-header-background);
  color: var(--theme-header-text);
}
.evaluation-deck__navigation-button--next:hover:not(:disabled) {
  background: var(--theme-banner-background);
  border-color: var(--theme-header-background);
  color: var(--theme-banner-text);
}
.evaluation-deck__navigation-button--next:not(:disabled)::v-deep .v-icon {
  color: currentColor;
}
.evaluation-deck__navigation-button:disabled {
  background: var(--theme-surface-soft);
  border-color: var(--theme-border-decorative);
  color: var(--text-secondary);
  cursor: not-allowed;
  opacity: 0.45;
}
.evaluation-deck__result {
  align-items: center;
  background: var(--theme-banner-background);
  border: 1px solid var(--theme-banner-border);
  border-radius: 14px;
  display: flex;
  gap: 13px;
  padding: 17px 18px;
}
.evaluation-deck__result > span {
  align-items: center;
  background: var(--surface-primary);
  border-radius: 10px;
  display: flex;
  flex: 0 0 auto;
  height: 44px;
  justify-content: center;
  width: 44px;
}
.evaluation-deck__result strong { color: var(--theme-banner-text); display: block; font-size: 16px; }
.evaluation-deck__result p { color: var(--text-secondary); font-size: 13px; margin: 3px 0 0; }
.evaluation-deck__submit {
  align-items: center;
  background: var(--theme-header-background);
  border: 0;
  border-radius: 10px;
  color: var(--theme-header-text);
  cursor: pointer;
  display: inline-flex;
  font-size: 14px;
  font-weight: 800;
  gap: 8px;
  margin-top: 18px;
  min-height: 44px;
  padding: 10px 17px;
}
.evaluation-deck__submit:hover:not(:disabled) { filter: brightness(0.9); }
.evaluation-deck__submit::v-deep .v-icon { color: currentColor !important; }
.evaluation-deck__submit:disabled { cursor: not-allowed; opacity: .45; }
.evaluation-deck__toast-content { align-items: center; display: flex; gap: 11px; }
.evaluation-deck__toast-content > span { align-items: center; background: var(--theme-surface-decorative); border-radius: 9px; display: flex; flex: 0 0 auto; height: 36px; justify-content: center; width: 36px; }
.evaluation-deck__toast-content::v-deep .v-icon { color: var(--theme-header-background); }
.evaluation-deck__toast-content strong { color: #172033; display: block; font-size: 13px; }
.evaluation-deck__toast-content p { color: #667085; font-size: 12px; margin: 2px 0 0; }
.evaluation-deck__toast::v-deep .v-snack__wrapper { background: #fff !important; border: 1px solid var(--theme-border-decorative); border-left: 4px solid var(--theme-header-background); border-radius: 12px; box-shadow: 0 10px 30px rgba(23, 32, 51, .18); color: #172033; min-width: 360px; }
.evaluation-deck__toast::v-deep .v-snack__content { padding: 12px 14px; }
.evaluation-deck__toast::v-deep .v-snack__action .v-icon { color: #667085 !important; }
@media (max-width: 600px) {
  .evaluation-deck__navigation { flex-direction: column; }
  .evaluation-deck__navigation-button { justify-content: center; width: 100%; }
  .evaluation-deck__toast::v-deep .v-snack__wrapper { min-width: 0; }
}
</style>
